/* Copyright (c) 2026 - Svayam Infoware Pvt. Ltd. All rights reserved. */
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { AskCitation } from '../../features/document-ocr/document-ocr.models';
import { DocumentOcrService } from '../../features/document-ocr/document-ocr.service';
import { AuthService } from './auth.service';

export interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  citations?: AskCitation[];
  isError?: boolean;
}

export type ChatTurn = { que: string; ans: string };

const SESSION_KEY = 'askAi.chatSessionId';
const TURNS_KEY = 'askAi.chatTurns';
const CASE_MARKER_RE = /\{\{CASE:([\d,\s]+)\}\}/g;

function newChatSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `ask-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Prefer localStorage so refresh (and some browser rebuilds) keep the same session. */
function readStoredSessionId(): string | null {
  try {
    const fromLocal = localStorage.getItem(SESSION_KEY);
    if (fromLocal) return fromLocal;
    const fromSession = sessionStorage.getItem(SESSION_KEY);
    if (fromSession) {
      localStorage.setItem(SESSION_KEY, fromSession);
      return fromSession;
    }
    return null;
  } catch {
    return null;
  }
}

function writeStoredSessionId(id: string): void {
  try {
    localStorage.setItem(SESSION_KEY, id);
    sessionStorage.setItem(SESSION_KEY, id);
  } catch {
    /* private mode / blocked storage — in-memory signal still works */
  }
}

function initialSessionId(): string {
  const stored = readStoredSessionId();
  if (stored) return stored;
  const id = newChatSessionId();
  writeStoredSessionId(id);
  return id;
}

function readCachedTurns(sessionId: string): ChatTurn[] {
  try {
    const raw = localStorage.getItem(TURNS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { sessionId?: string; turns?: ChatTurn[] };
    if (!parsed || parsed.sessionId !== sessionId || !Array.isArray(parsed.turns)) return [];
    return parsed.turns.filter((t) => t && (t.que || t.ans));
  } catch {
    return [];
  }
}

function writeCachedTurns(sessionId: string, turns: ChatTurn[]): void {
  try {
    localStorage.setItem(TURNS_KEY, JSON.stringify({ sessionId, turns }));
  } catch {
    /* ignore */
  }
}

function clearCachedTurns(): void {
  try {
    localStorage.removeItem(TURNS_KEY);
  } catch {
    /* ignore */
  }
}

/** Build citation stubs from {{CASE:id}} markers so restored bubbles still show cards. */
export function citationsFromAnswerText(text: string): AskCitation[] {
  const ids = new Set<number>();
  const re = new RegExp(CASE_MARKER_RE);
  let match: RegExpExecArray | null;
  while ((match = re.exec(String(text || ''))) !== null) {
    for (const part of match[1].split(',')) {
      const id = Number(part.trim());
      if (Number.isFinite(id) && id > 0) ids.add(id);
    }
  }
  return [...ids].map((grievanceId) => ({ grievanceId, subject: `Case ${grievanceId}` }));
}

/** Map DB conversation_json [{que,ans}] into chat bubbles. */
export function turnsToChatMessages(
  turns: Array<{ que?: string; ans?: string }>,
): ChatMessage[] {
  const out: ChatMessage[] = [];
  let id = 1;
  for (const t of turns || []) {
    const que = String(t?.que ?? '').trim();
    const ans = String(t?.ans ?? '').trim();
    if (que) out.push({ id: id++, role: 'user', text: que });
    if (ans) {
      out.push({
        id: id++,
        role: 'assistant',
        text: ans,
        citations: citationsFromAnswerText(ans),
      });
    }
  }
  return out;
}

/** Pair user/assistant bubbles back into [{que,ans}] for local restore. */
export function messagesToTurns(messages: ChatMessage[]): ChatTurn[] {
  const turns: ChatTurn[] = [];
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (m.role !== 'user') continue;
    const next = messages[i + 1];
    if (next?.role === 'assistant' && !next.isError) {
      turns.push({ que: m.text, ans: next.text });
    }
  }
  return turns;
}

/**
 * Single source of truth for the case-assistant conversation — shared by the floating
 * widget (layout/chat-widget, contextual per-case quick-ask) and the full-page assistant
 * (features/assistant, open-ended browsing), so asking a question in one and switching to
 * the other doesn't lose the thread.
 */
@Injectable({ providedIn: 'root' })
export class ChatWidgetService {
  private readonly ocr = inject(DocumentOcrService);
  private readonly auth = inject(AuthService);

  readonly isOpen = signal(false);
  readonly scopedCaseId = signal<number | string | null>(null);
  /** Bumped when the composer should receive focus (e.g. after "Ask AI about this case"). */
  readonly focusComposerTick = signal(0);

  readonly query = signal('');
  readonly asking = signal(false);
  readonly messages = signal<ChatMessage[]>([]);
  readonly restoring = signal(true);
  /**
   * Groups Q&A turns in ai_conversations — persisted in localStorage so refresh
   * keeps the same DB row / JSON array (and a local turns cache as fallback).
   */
  readonly sessionId = signal(initialSessionId());

  readonly suggestions = computed(() =>
    this.scopedCaseId() != null
      ? ['What is the applicant asking for?', 'Who is this case forwarded to?', 'Summarize this case in one line']
      : ['Which cases involve a land dispute?', 'Summarize the most recent case', 'List cases from Devaria tehsil'],
  );

  constructor() {
    let prevScope: number | string | null | undefined;
    let booted = false;
    effect(() => {
      const caseId = this.scopedCaseId();
      if (!booted) {
        booted = true;
        prevScope = caseId;
        void this.hydrateOrGreet(caseId);
        return;
      }
      if (caseId === prevScope) return;
      const previous = prevScope;
      prevScope = caseId;
      // Entering / switching a case starts a fresh thread. Clearing scope back to
      // "all cases" keeps the current thread (and its sessionId) so refresh works.
      if (caseId != null && caseId !== previous) {
        this.rotateSession();
        this.messages.set([this.greetingFor(caseId)]);
      }
    });
  }

  private greetingFor(caseId: number | string | null): ChatMessage {
    return {
      id: 1,
      role: 'assistant',
      text:
        caseId != null
          ? 'Asking about this case only.'
          : 'Ask about any confirmed case — a name, village, or topic.',
    };
  }

  private async hydrateOrGreet(caseId: number | string | null): Promise<void> {
    this.restoring.set(true);
    try {
      await this.auth.whenSessionReady();
      const sid = this.sessionId() || readStoredSessionId();
      if (sid) {
        if (sid !== this.sessionId()) this.sessionId.set(sid);
        writeStoredSessionId(sid);

        let turns: ChatTurn[] = [];
        if (this.auth.isAuthenticated()) {
          try {
            const row = await this.ocr.getAskConversation(sid);
            if (Array.isArray(row?.conversationJson) && row.conversationJson.length) {
              turns = row.conversationJson.map((t) => ({
                que: String(t?.que ?? ''),
                ans: String(t?.ans ?? ''),
              }));
            }
          } catch {
            /* fall through to local cache */
          }
        }

        if (!turns.length) {
          turns = readCachedTurns(sid);
        }

        if (turns.length) {
          writeCachedTurns(sid, turns);
          this.messages.set(turnsToChatMessages(turns));
          return;
        }
      }
      this.messages.set([this.greetingFor(caseId)]);
    } catch {
      this.messages.set([this.greetingFor(caseId)]);
    } finally {
      this.restoring.set(false);
    }
  }

  private rotateSession(): void {
    const id = newChatSessionId();
    writeStoredSessionId(id);
    this.sessionId.set(id);
    clearCachedTurns();
  }

  private persistMessagesToCache(): void {
    const sid = this.sessionId();
    if (!sid) return;
    const turns = messagesToTurns(this.messages());
    if (turns.length) writeCachedTurns(sid, turns);
  }

  toggle(): void {
    this.isOpen.update((v) => !v);
  }

  /** Opens the widget without changing whatever scope it already has. */
  openGlobal(): void {
    this.isOpen.set(true);
  }

  /**
   * Opens the widget scoped to one case — used by "Ask AI about this case".
   * Optional `draft` is prefilled in the composer so the officer can press Enter.
   */
  openForCase(caseId: number | string, draft?: string): void {
    this.scopedCaseId.set(caseId);
    if (draft?.trim()) this.query.set(draft.trim());
    this.isOpen.set(true);
    this.focusComposerTick.update((n) => n + 1);
  }

  close(): void {
    this.isOpen.set(false);
  }

  clearScope(): void {
    this.scopedCaseId.set(null);
  }

  async ask(question = this.query()): Promise<void> {
    const text = question.trim();
    if (!text || this.asking() || this.restoring()) return;

    // Re-read storage in case another tab rotated; never drop an empty sessionId.
    const sid = this.sessionId() || readStoredSessionId() || initialSessionId();
    if (sid !== this.sessionId()) this.sessionId.set(sid);
    writeStoredSessionId(sid);

    this.messages.update((messages) => [...messages, { id: Date.now(), role: 'user', text }]);
    this.query.set('');
    this.asking.set(true);

    try {
      const result = await this.ocr.ask(text, this.scopedCaseId() ?? undefined, sid);
      this.messages.update((messages) => [
        ...messages,
        { id: Date.now() + 1, role: 'assistant', text: result.answer, citations: result.citations },
      ]);
      this.persistMessagesToCache();
    } catch (err) {
      this.messages.update((messages) => [
        ...messages,
        {
          id: Date.now() + 1,
          role: 'assistant',
          text: err instanceof Error ? err.message : 'Something went wrong answering that.',
          isError: true,
        },
      ]);
    } finally {
      this.asking.set(false);
    }
  }

  clearChat(): void {
    this.rotateSession();
    this.messages.set([this.greetingFor(this.scopedCaseId())]);
  }
}
